import { Injectable } from '@angular/core';
import { Observable, map, shareReplay, switchMap } from 'rxjs';
import { SportFacility } from '@models';
import { documentId, where } from '@angular/fire/firestore';
import { FirestoreService } from './firestore.service';
import { TenantContextService } from './tenant-context.service';

@Injectable({
  providedIn: 'root',
})
export class SportFacilityService {
  public all$: Observable<SportFacility[]>;

  constructor(
    private readonly firestore: FirestoreService,
    private readonly tenantContext: TenantContextService,
  ) {
    this.all$ = this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<SportFacility>(
          this.tenantContext.collections.SPORT_FACILITIES,
        ),
      ),
      shareReplay(1),
    );
  }

  getActive$(): Observable<SportFacility[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<SportFacility>(
          this.tenantContext.collections.SPORT_FACILITIES,
          where('active', '==', true),
        ),
      ),
      shareReplay(1),
    );
  }

  /**
   * Facilities that the reservations module may offer: active and not
   * explicitly marked as non-bookable. A facility without the `bookable`
   * field is bookable (backward compatible — see `SportFacility.bookable`).
   */
  getBookable$(): Observable<SportFacility[]> {
    return this.getActive$().pipe(
      map((facilities) => facilities.filter((f) => f.bookable !== false)),
    );
  }

  /**
   * Venues where matches are played: active and explicitly marked as NOT
   * bookable. Disjoint with `getBookable$()` — no facility shows up in both
   * (D10). Feeds the facilities screen and the venue selectors of the club
   * and team forms.
   *
   * Filtered on the client on purpose: `where('bookable','==',false)` would
   * work here, but keeping both lists derived from the same `getActive$()`
   * query keeps them provably disjoint and adds no new index.
   */
  getVenues$(): Observable<SportFacility[]> {
    return this.getActive$().pipe(
      map((facilities) => facilities.filter((f) => f.bookable === false)),
    );
  }

  /**
   * Single facility by id. Emits `null` when the document does not exist or
   * is soft-deleted, so a broken reference does not hang the caller.
   * Implemented with `findDocument` on purpose: `getDocument()` filters out
   * `undefined` and never emits for a missing document.
   */
  getById$(id: string): Observable<SportFacility | null> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.findDocument<SportFacility>(
          this.tenantContext.collections.SPORT_FACILITIES,
          where(documentId(), '==', id),
        ),
      ),
    );
  }

  async create(data: Omit<SportFacility, 'id'>): Promise<string> {
    return this.firestore.create<SportFacility>(
      this.tenantContext.collections.SPORT_FACILITIES,
      data,
    );
  }

  async update(data: Partial<SportFacility> & { id: string }): Promise<void> {
    return this.firestore.update<SportFacility>(
      this.tenantContext.collections.SPORT_FACILITIES,
      data,
    );
  }

  async delete(id: string): Promise<void> {
    return this.firestore.delete(
      this.tenantContext.collections.SPORT_FACILITIES,
      id,
    );
  }
}
